<script>
    import Layout from "../../../layouts/main";
    import PageHeader from "@/components/page-header";
    import appConfig from "@/app.config";
    import Loader from "../../../../components/widgets/loader";
    import Forms from "../../../../mixins/Forms";

    import axios from 'axios';

    /**
     * View users component
     */
    export default {
        page: {
            title: "All Users",
            meta: [{name: "description", content: appConfig.description}]
        },
        mixins: [Forms],
        components: {Layout, PageHeader, Loader},
        data() {
            return {
                title: "All Users",
                items: [
                    {
                        text: "Settings",
                        href: "#"
                    },
                    {
                        text: "All Users",
                        active: true
                    }
                ],

                isLoading: false,
                users: null,
                serverErrors: [],
                filterType: 'internal', // 'all', 'internal', 'external'
                filterOptions: [
                    { value: 'all', text: 'All Users' },
                    { value: 'internal', text: 'Internal Users' },
                    { value: 'external', text: 'External Users' }
                ],
                
                // Add External User Modal
                showAddExternalUserModal: false,
                newExternalUser: {
                    name: '',
                    email: ''
                },
                isAddingExternalUser: false,
                
                // Confirm Modal
                showConfirmModal: false,
                confirmAction: null,
                confirmUser: null
            };
        },
        computed: {
            filteredUsers() {
                if (!this.users) return [];
                
                switch (this.filterType) {
                    case 'internal':
                        return this.users.filter(user => !user.external);
                    case 'external':
                        return this.users.filter(user => user.external);
                    default:
                        return this.users;
                }
            }
        },
        methods: {
            loadUsers() {
                let vm = this;
                vm.isLoading = true;

                vm.isSaving = true;
                axios.get(API_BASE + '/users/').then(r => {
                    vm.isLoading = false;
                    vm.users = r.data;
                }).catch(e => {
                    console.log(e);
                    vm.isLoading = false;
                    vm.setErrors(e);
                });
            },
            
            // Add External User methods
            openAddExternalUserModal() {
                this.newExternalUser = {
                    name: '',
                    email: ''
                };
                this.serverErrors = [];
                this.showAddExternalUserModal = true;
            },
            
            closeAddExternalUserModal() {
                this.showAddExternalUserModal = false;
                this.newExternalUser = {
                    name: '',
                    email: ''
                };
                this.serverErrors = [];
            },
            
            addExternalUser() {
                let vm = this;
                
                if (!this.newExternalUser.name || !this.newExternalUser.email) {
                    this.serverErrors = ['Name and email are required'];
                    return;
                }
                
                vm.isAddingExternalUser = true;
                vm.serverErrors = [];
                
                axios.post(API_BASE + '/users/add-external-user', {
                    name: this.newExternalUser.name,
                    email: this.newExternalUser.email
                }).then(r => {
                    vm.isAddingExternalUser = false;
                    vm.closeAddExternalUserModal();
                    vm.loadUsers(); // Reload users list
                    vm.$notify.success({
                        title: 'Success!',
                        message: 'External user added successfully'
                    });
                }).catch(e => {
                    vm.isAddingExternalUser = false;
                    vm.setErrors(e);
                });
            },
            
            // Enable/Disable User methods
            openConfirmModal(user) {
                this.confirmUser = user;
                this.confirmAction = user.enabled ? 'disable' : 'enable';
                this.showConfirmModal = true;
            },
            
            closeConfirmModal() {
                this.showConfirmModal = false;
                this.confirmUser = null;
                this.confirmAction = null;
            },
            
            confirmToggleUserStatus() {
                let vm = this;
                const user = this.confirmUser;
                const action = this.confirmAction;
                
                vm.serverErrors = [];
                vm.closeConfirmModal();
                
                axios.post(API_BASE + '/users/' + user.id + '/' + action).then(r => {
                    vm.loadUsers(); // Reload users list
                    vm.$notify.success({
                        title: 'Success!',
                        message: `User ${action}d successfully`
                    });
                }).catch(e => {
                    vm.setErrors(e);
                });
            }
        },
        created() {
            this.loadUsers();
        }
    };
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items"/>
        <errors :error-list="serverErrors"></errors>

        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <div v-if="isLoading" class="text-center">
                            <b-spinner class="m-2" variant="itg-dark" role="status"></b-spinner>
                        </div>

                        <div v-if="!isLoading">
                            <!-- Filter Section -->
                            <div class="row mb-3 align-items-end">
                                <div class="col-md-3">
                                    <label for="userFilter" class="form-label mb-1">Filter Users</label>
                                    <b-form-select
                                        id="userFilter"
                                        v-model="filterType"
                                        :options="filterOptions"
                                        class="mb-0"
                                    />
                                </div>
                                <div class="col-md-9 text-right">
                                    <button 
                                        type="button" 
                                        class="btn btn-itg-dark"
                                        @click="openAddExternalUserModal"
                                    >
                                        <i class="bx bx-plus mr-1"></i>
                                        Add External User
                                    </button>
                                </div>
                            </div>

                            <div class="table-responsive mb-0">
                                <table class="table mb-0 table-centered">
                                    <thead class="thead-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>External</th>
                                        <th>Enabled</th>
                                        <th>Escalate WhatsApp</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="user in filteredUsers" :key="user.id">
                                        <th scope="row">{{ user.id }}</th>
                                        <td>{{ user.name }}</td>
                                        <td>{{ user.email }}</td>
                                        <td>
                                            <i v-if="user.external" class="bx bx-check text-success" style="font-size: 1.2rem;"></i>
                                            <i v-else class="bx bx-x text-danger" style="font-size: 1.2rem;"></i>
                                        </td>
                                        <td>
                                            <i v-if="user.enabled" class="bx bx-check text-success" style="font-size: 1.2rem;"></i>
                                            <i v-else class="bx bx-x text-danger" style="font-size: 1.2rem;"></i>
                                        </td>
                                        <td>
                                            <i v-if="user.escalate_whatsapp" class="bx bx-check text-success" style="font-size: 1.2rem;"></i>
                                            <i v-else class="bx bx-x text-danger" style="font-size: 1.2rem;"></i>
                                        </td>
                                        <td class="text-right">
                                            <!-- Edit button for internal users -->
                                            <button v-if="!user.external"
                                                    type="button"
                                                    class="btn btn-itg btn-sm"
                                                    @click="$router.push('/settings/users/'+user.id+'/edit')"
                                            >
                                                Edit
                                            </button>
                                            
                                            <!-- Enable/Disable buttons -->
                                            <button 
                                                type="button"
                                                class="btn btn-itg btn-sm ml-1"
                                                @click="openConfirmModal(user)"
                                                :title="user.enabled ? 'Disable user' : 'Enable user'"
                                            >
                                                {{ user.enabled ? 'Disable' : 'Enable' }}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="filteredUsers.length === 0">
                                        <th scope="row" colspan="5">
                                            <span class="text-danger">No results</span>
                                        </th>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Add External User Modal -->
        <b-modal 
            v-model="showAddExternalUserModal" 
            title="Add External User" 
            centered
            @hidden="closeAddExternalUserModal"
            :hide-footer="true"
        >
            <errors :error-list="serverErrors"></errors>
            
            <form @submit.prevent="addExternalUser">
                <div class="form-group">
                    <label for="externalUserName" class="form-label">Name *</label>
                    <input
                        id="externalUserName"
                        v-model="newExternalUser.name"
                        type="text"
                        class="form-control"
                        placeholder="Enter user name"
                        required
                    />
                </div>
                
                <div class="form-group">
                    <label for="externalUserEmail" class="form-label">Email *</label>
                    <input
                        id="externalUserEmail"
                        v-model="newExternalUser.email"
                        type="email"
                        class="form-control"
                        placeholder="Enter user email"
                        required
                    />
                </div>
                
                <div class="text-right mt-4">
                    <button 
                        type="button" 
                        class="btn btn-itg mr-2"
                        @click="closeAddExternalUserModal"
                        :disabled="isAddingExternalUser"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        class="btn btn-itg-dark"
                        :disabled="isAddingExternalUser"
                    >
                        <b-spinner v-if="isAddingExternalUser" small class="mr-1"></b-spinner>
                        {{ isAddingExternalUser ? 'Adding...' : 'Add External User' }}
                    </button>
                </div>
            </form>
        </b-modal>
        
        <!-- Confirm Modal -->
        <b-modal 
            v-model="showConfirmModal" 
            :title="`${confirmAction === 'enable' ? 'Enable' : 'Disable'} User`"
            centered
            @hidden="closeConfirmModal"
        >
            <p>
                Are you sure you want to {{ confirmAction }} 
                <strong>{{ confirmUser ? confirmUser.name : '' }}</strong>?
            </p>
            
            <template v-slot:modal-footer>
                <button 
                    type="button" 
                    class="btn btn-itg mr-2"
                    @click="closeConfirmModal"
                >
                    Cancel
                </button>
                <button 
                    type="button" 
                    class="btn btn-itg-dark"
                    @click="confirmToggleUserStatus"
                >
                    {{ confirmAction === 'enable' ? 'Enable' : 'Disable' }} User
                </button>
            </template>
        </b-modal>
        <!-- end row -->
    </Layout>
</template>
